import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation, Trans } from 'react-i18next';
import { getIntlLocale } from '../../i18n';
import AdminSidebar from '../../components/AdminSidebar';
import theme from '../../theme';
import {
  Plus, X, Save, Unlock, RefreshCw, Map as MapIcon, List,
  Pencil, Trash2, Search, Phone, Clock, FileText, MapPin, Hash,
  Activity, ShieldCheck, ShieldAlert, AlertTriangle, KeyRound, ChevronDown, ChevronRight,
  Monitor, Wifi
} from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/img/leaflet/marker-icon-2x.png',
  iconUrl:       '/img/leaflet/marker-icon.png',
  shadowUrl:     '/img/leaflet/marker-shadow.png',
});

const goldIcon = new L.Icon({
  iconUrl: '/img/leaflet/marker-icon-gold.png',
  shadowUrl: '/img/leaflet/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const blueIcon = new L.Icon({
  iconUrl: '/img/leaflet/marker-icon.png',
  shadowUrl: '/img/leaflet/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const C = {
  gold:       theme.gold,
  goldLight:  theme.creamBone,
  goldBorder: theme.sand,
  dark:       theme.ink,
  mid:        theme.grey600,
  muted:      theme.grey400,
  faint:      theme.creamDeep,
  white:      theme.white,
  border:     theme.creamBone,
  green:      theme.greenDeep,
  blue:       theme.blue,
  amber:      theme.amber,
  red:        theme.redBrick,
};

const DEFAULT_CENTER = [35.1264, 33.4299];
const DEFAULT_ZOOM   = 9;
const EMPTY_FORM = { name:'', unit_id:'', address:'', phone:'', opening_hours:'', notes:'', latitude:'', longitude:'' };

// Callback feed presentation
const ENDPOINT_CFG = {
  save: { bg: theme.blueWashSoft, color: C.blue  },
  take: { bg: theme.greenWashSoft, color: C.green },
};
const OUTCOME_COLOR = (outcome) => {
  if (!outcome) return C.muted;
  if (outcome === 'applied') return C.green;
  if (outcome.startsWith('rejected') || outcome === 'error') return C.red;
  return C.amber;
};

// ── Map helpers ────────────────────────────────────────────────────────────────
function FlyToHandler({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 15, { duration: 1.2 });
  }, [target, map]);
  return null;
}
function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: e => onMapClick(e.latlng) });
  return null;
}

// ── Geocoding: Photon + Nominatim in parallel, merged ─────────────────────────

async function _photon(q) {
  // NOTE: lang=el is NOT supported by Photon — do not add it
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=5&bbox=19.0,34.0,35.0,42.0`;
  const r = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!r.ok) throw new Error('Photon failed');
  const data = await r.json();
  return (data.features || []).map(f => {
    const p = f.properties;
    const [lng, lat] = f.geometry.coordinates; // GeoJSON = [lon, lat]
    const street  = [p.street, p.housenumber].filter(Boolean).join(' ');
    const primary = (p.name && p.name !== p.street) ? p.name : (street || p.city || p.county || '');
    const secondary = [
      street && primary !== street ? street : null,
      p.city || p.town || p.village || p.county,
      p.state,
    ].filter(Boolean).join(', ');
    return { lat, lng, primary: primary.trim(), secondary: secondary.trim() };
  }).filter(r => r.primary);
}

async function _nominatim(q) {
  // viewbox gives a soft bias toward Greece+Cyprus (lon_min,lat_max,lon_max,lat_min)
  const params = new URLSearchParams({
    q, format: 'json', limit: '5', addressdetails: '1',
    viewbox: '19.0,42.0,35.0,34.0', bounded: '0',
  });
  const r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
    headers: { Accept: 'application/json', 'Accept-Language': 'el,en' },
  });
  if (!r.ok) throw new Error('Nominatim failed');
  const data = await r.json();
  return data.map(item => {
    const a = item.address || {};
    const road = [a.road, a.house_number].filter(Boolean).join(' ');
    const place = a.amenity || a.shop || a.leisure || a.building || '';
    const primary = (place || road || a.suburb || a.neighbourhood || item.display_name.split(',')[0]).trim();
    const city = a.city || a.town || a.village || a.municipality || '';
    const secondary = [city, a.state || a.state_district, a.country].filter(Boolean).join(', ');
    return { lat: parseFloat(item.lat), lng: parseFloat(item.lon), primary, secondary };
  }).filter(r => r.primary);
}

async function geocodeSearch(q) {
  const [ph, nm] = await Promise.allSettled([_photon(q), _nominatim(q)]);
  const results = [];
  const seen = new Set();
  const add = item => {
    const key = `${item.lat.toFixed(2)},${item.lng.toFixed(2)}`;
    if (!seen.has(key)) { seen.add(key); results.push(item); }
  };
  if (ph.status === 'fulfilled') ph.value.forEach(add);
  if (nm.status === 'fulfilled') nm.value.forEach(add);
  return results.slice(0, 6);
}

// ── AddressSearch component ────────────────────────────────────────────────────
function AddressSearch({ value, onChange, onPick }) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [hovered, setHovered]         = useState(-1);
  const ref = React.useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setSuggestions([]); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  // Debounced auto-search as user types
  useEffect(() => {
    if (!value || value.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => runSearch(value), 500);
    return () => clearTimeout(t);
  }, [value]); // eslint-disable-line

  const runSearch = async (q) => {
    setLoading(true);
    try {
      const data = await geocodeSearch(q);
      setSuggestions(data);
      setHovered(-1);
      if (!data.length) toast(t('admin.lockers.noResults'), { icon: '🔍' });
    } catch { toast.error(t('admin.lockers.searchError')); }
    finally { setLoading(false); }
  };

  const pick = (s) => {
    onPick(s);
    setSuggestions([]);
  };

  return (
    <div ref={ref}>
      <FieldLabel icon={<MapPin size={13} />}>{t('admin.lockers.fieldAddress')}</FieldLabel>
      <div style={{ position: 'relative' }}>
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={t('admin.lockers.addressPlaceholder')}
          style={{ ...inputSt, paddingRight: 42 }}
          autoComplete="off"
        />
        {/* Search indicator */}
        <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: loading ? C.gold : C.muted, pointerEvents: 'none' }}>
          {loading ? <span style={{ fontSize: 14 }}>⏳</span> : <Search size={14} />}
        </div>
      </div>
      <p style={{ margin: '5px 0 0', fontSize: 11, color: C.muted }}>{t('admin.lockers.addressHint')}</p>

      {suggestions.length > 0 && (
        <div style={{ marginTop: 4, background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', boxShadow: `0 8px 28px ${theme.alpha.black(0.12)}`, position: 'relative', zIndex: 9999 }}>
          {suggestions.map((s, i) => (
            <button key={i} type="button"
              onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(-1)}
              onClick={() => pick(s)}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', padding: '10px 14px', background: hovered === i ? C.faint : 'transparent', border: 'none', borderBottom: i < suggestions.length - 1 ? `1px solid ${C.faint}` : 'none', cursor: 'pointer', textAlign: 'left', fontFamily: theme.font.body }}>
              <MapPin size={13} style={{ flexShrink: 0, marginTop: 3, color: C.gold }} />
              <div>
                <div style={{ fontSize: 13, color: C.dark, lineHeight: 1.3 }}>{s.primary}</div>
                {s.secondary && <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.3 }}>{s.secondary}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminLockers() {
  const { t } = useTranslation();
  const [machines, setMachines]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('map');
  const [showAdd, setShowAdd]     = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [editMachine, setEditMachine] = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [checkingId, setCheckingId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [expanded, setExpanded]   = useState(null);
  const [boxInput, setBoxInput]   = useState({});
  const [unlocking, setUnlocking] = useState(null);
  const [callbacks, setCallbacks] = useState([]);
  const [cbMeta, setCbMeta]       = useState(null);
  const [cbLoading, setCbLoading] = useState(false);
  const [cbFilter, setCbFilter]   = useState('all');
  const [cbExpanded, setCbExpanded] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pickups, setPickups]     = useState([]);
  const [pkLoading, setPkLoading] = useState(false);
  const [pkError, setPkError]     = useState(null);
  const [registering, setRegistering] = useState(null);
  const [manualId, setManualId]   = useState('');
  const [manualForce, setManualForce] = useState(false);
  // Screen settings, per machine: { loading, saving, error, pushed, form }.
  // Keyed rather than single-valued because the cards expand independently and
  // an operator comparing two cabinets' support numbers has both open.
  const [screen, setScreen]       = useState({});

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    loadMachines();
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []); // eslint-disable-line

  const loadMachines = async () => {
    setLoading(true);
    try {
      setMachines(await api.get('/locker/admin/machines'));
      setLoadError(null);
    } catch (err) {
      // This used to be try/finally with no catch: a failed request left
      // `machines` at [] and the page rendered "No machines yet — add your
      // first". An outage shown as an empty inventory is the one reading that
      // invites an admin to "fix" it by re-creating machines that already exist.
      setLoadError(err.error || t('admin.lockers.loadFailed'));
      toast.error(t('admin.lockers.loadFailed'));
    } finally { setLoading(false); }
  };

  // ── Stranded pickups ───────────────────────────────────────────────────────
  // Rentals that were paid but never got a Bestwond take code. The customer has
  // been charged and has nothing to type into the machine, so this is the one
  // list on this page a human has to act on.
  const loadPickups = useCallback(async () => {
    setPkLoading(true);
    try {
      const data = await api.get('/locker/admin/pending-pickups');
      setPickups(data.rentals || []);
      setPkError(null);
    } catch (err) {
      setPkError(err.error || t('admin.lockers.pickupsLoadFailed'));
    } finally { setPkLoading(false); }
  }, [t]);

  useEffect(() => {
    if (tab === 'pickups') loadPickups();
  }, [tab, loadPickups]);

  const registerPickup = async (rentalId, force = false) => {
    const id = (rentalId || '').trim();
    if (!id) { toast.error(t('admin.lockers.pickupIdRequired')); return; }
    setRegistering(id);
    try {
      const result = await api.post(`/locker/admin/rentals/${id}/register-pickup`, force ? { force: true } : {});
      toast.success(t('admin.lockers.pickupRegistered', { code: result.take_code }));
      loadPickups();
    } catch (err) {
      toast.error(err.error || t('admin.lockers.pickupRegisterFailed'));
    } finally { setRegistering(null); }
  };

  // ── The cabinet's own screen ───────────────────────────────────────────────
  // Bestwond's API can set three things on the machine's home screen — a QR
  // code, an address line and a help phone — and offers no way to read back what
  // a unit is currently showing. So the panel shows what WE last sent and when,
  // and is explicit that this is a record of a push rather than the machine's
  // live state.
  const loadScreen = useCallback(async (machineId) => {
    setScreen(p => ({ ...p, [machineId]: { ...(p[machineId] || {}), loading: true, error: null } }));
    try {
      const data = await api.get(`/locker/admin/machines/${machineId}/screen`);
      setScreen(p => ({
        ...p,
        [machineId]: {
          loading: false, saving: false, error: null,
          pushed: data.pushed, simulate: data.simulate,
          // Pre-filled from the last push, or derived from the machine's own
          // address and the published support number. An operator who just
          // wants the defaults on the screen presses one button.
          form: { ...data.proposed },
        },
      }));
    } catch (err) {
      setScreen(p => ({
        ...p,
        [machineId]: { ...(p[machineId] || {}), loading: false, error: err.error || t('admin.lockers.screenLoadFailed') },
      }));
    }
  }, [t]);

  const setScreenField = (machineId, field, value) =>
    setScreen(p => ({ ...p, [machineId]: { ...p[machineId], form: { ...p[machineId].form, [field]: value } } }));

  const pushScreen = async (machine) => {
    const state = screen[machine.id];
    if (!state || !state.form) return;
    const { qr, device_address, app_kf_mobile } = state.form;
    if (!qr || !device_address || !app_kf_mobile) {
      toast.error(t('admin.lockers.screenFieldsRequired'));
      return;
    }
    // The phone on the screen is where a customer turns when a box will not
    // open, so it is worth one confirmation that names the number being sent —
    // the same reasoning as the unlock confirm, without the physical door.
    if (!window.confirm(t('admin.lockers.screenConfirm', {
      unit: machine.name || machine.unit_id, phone: app_kf_mobile,
    }))) return;

    setScreen(p => ({ ...p, [machine.id]: { ...p[machine.id], saving: true } }));
    try {
      const res = await api.post(`/locker/admin/machines/${machine.id}/screen`, {
        qr, device_address, app_kf_mobile,
      });
      toast.success(res.simulated ? t('admin.lockers.screenPushedSim') : t('admin.lockers.screenPushed'));
      setScreen(p => ({
        ...p,
        [machine.id]: { ...p[machine.id], saving: false, pushed: res.pushed },
      }));
    } catch (err) {
      setScreen(p => ({ ...p, [machine.id]: { ...p[machine.id], saving: false } }));
      toast.error(err.error || t('admin.lockers.screenPushFailed'));
    }
  };

  const toggleExpand = (lockerId) => {
    setExpanded(p => {
      const next = p === lockerId ? null : lockerId;
      // Fetched on open rather than with the machine list: it is one request per
      // card the operator actually looks at, instead of one per cabinet on a
      // page most visits never expand.
      if (next && !screen[lockerId]) loadScreen(lockerId);
      return next;
    });
  };

  const loadCallbacks = useCallback(async (filter) => {
    setCbLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (filter === 'save' || filter === 'take') params.set('endpoint', filter);
      if (filter === 'invalid') params.set('invalid', 'true');
      const data = await api.get(`/locker/admin/callbacks?${params.toString()}`);
      setCallbacks(data.callbacks || []);
      setCbMeta({ signature_mode: data.signature_mode, max_age_seconds: data.max_age_seconds });
    } catch { toast.error(t('admin.lockers.callbacksLoadFailed')); }
    finally { setCbLoading(false); }
  }, [t]);

  useEffect(() => {
    if (tab === 'callbacks') loadCallbacks(cbFilter);
  }, [tab, cbFilter, loadCallbacks]);

  // The cabinet allocates its own boxes and reports them as box_name, so the
  // admin has to name the box to open. Backend takes lock_address (or slot_code).
  const handleUnlock = async (machine) => {
    const box = (boxInput[machine.id] || '').trim();
    if (!box) { toast.error(t('admin.lockers.boxNameRequired')); return; }
    // A manual unlock opens a physical box on a live cabinet by remote command,
    // with no undo. A fat-fingered box name (A12 → A21) opens a DIFFERENT
    // customer's occupied box and lets the item walk — so confirm the exact unit
    // and box before sending it, on both the Enter path and the button (follow-up
    // audit H3).
    const unitName = machine.name || machine.locker_location || machine.unit_id;
    if (!window.confirm(t('admin.lockers.unlockConfirm', { box, unit: unitName }))) return;
    setUnlocking(machine.id);
    try {
      await api.post('/locker/admin/unlock', { unit_id: machine.unit_id, lock_address: box });
      toast.success(t('admin.lockers.boxOpened', { box }));
      setBoxInput(p => ({ ...p, [machine.id]: '' }));
    } catch (err) { toast.error(err.error || t('admin.lockers.unlockFailed')); }
    finally { setUnlocking(null); }
  };

  const geoApply = useCallback((result, toForm, toPendingPin) => {
    const lat = parseFloat(result.lat.toFixed ? result.lat.toFixed(6) : parseFloat(result.lat).toFixed(6));
    const lng = parseFloat(result.lng.toFixed ? result.lng.toFixed(6) : parseFloat(result.lng).toFixed(6));
    // Build a human-readable address from Photon result
    const address = [result.primary, result.secondary].filter(Boolean).join(', ');
    toForm({ address, latitude: lat.toString(), longitude: lng.toString() });
    if (toPendingPin) toPendingPin({ lat, lng });
    setFlyTarget({ lat, lng });
    if (tab !== 'map') setTab('map');
    toast.success(t('admin.lockers.locationFound'), { icon: '📍' });
  }, [tab, t]);

  const handleMapClick = useCallback((latlng) => {
    if (showAdd || editMachine) return;
    const lat = parseFloat(latlng.lat.toFixed(6));
    const lng = parseFloat(latlng.lng.toFixed(6));
    setPendingPin({ lat, lng });
    setForm(f => ({ ...f, latitude: lat.toString(), longitude: lng.toString() }));
    setShowAdd(true);
  }, [showAdd, editMachine]);

  const closeAdd = () => { setShowAdd(false); setPendingPin(null); setForm(EMPTY_FORM); };

  const handleAddMachine = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/locker/admin/machines', {
        name: form.name, unit_id: form.unit_id, address: form.address,
        phone: form.phone, opening_hours: form.opening_hours, notes: form.notes,
        latitude:  form.latitude  ? parseFloat(form.latitude)  : undefined,
        longitude: form.longitude ? parseFloat(form.longitude) : undefined,
      });
      toast.success(t('admin.lockers.machineAdded')); closeAdd(); loadMachines();
    } catch (err) { toast.error(err.error || t('admin.lockers.error')); }
    finally { setSaving(false); }
  };

  // Ask this cabinet, right now, whether it is there.
  //
  // The health pill beside each machine is written by a cron that runs every
  // five minutes, which is the right cadence for an alert and the wrong one for
  // a person who is troubleshooting: after power-cycling a unit you want the
  // answer now, not on the next tick. GET /locker/status/:unitId existed for
  // exactly this and had no caller at all.
  //
  // It reports and does not persist — the cron owns health_status, and a manual
  // probe overwriting it would make the "last seen" timestamp mean two different
  // things. Note this is the one endpoint in the locker client that has never
  // been answered by real hardware, so an inconclusive result is reported as
  // inconclusive rather than dressed up as "offline".
  const handleCheckNow = async (m, e) => {
    e.stopPropagation();
    setCheckingId(m.id);
    try {
      const res = await api.get(`/locker/status/${encodeURIComponent(m.unit_id)}`);
      const down = res && (res.online === false || res.status === 'offline' || res.line_status === 0);
      if (down) toast.error(t('admin.lockers.checkOffline', { name: m.name }), { duration: 7000 });
      else if (res?.simulated) toast(t('admin.lockers.checkSimulated', { name: m.name }), { duration: 7000 });
      else toast.success(t('admin.lockers.checkOnline', { name: m.name }));
    } catch (err) {
      toast.error(err?.error || t('admin.lockers.checkFailed'), { duration: 8000 });
    } finally {
      setCheckingId(null);
    }
  };

  const openEdit = (m, e) => {
    e.stopPropagation(); setEditMachine(m);
    setEditForm({
      name: m.name||'', unit_id: m.unit_id||'', address: m.address||'',
      phone: m.phone||'', opening_hours: m.opening_hours||'', notes: m.notes||'',
      latitude:  m.latitude  ? String(parseFloat(m.latitude))  : '',
      longitude: m.longitude ? String(parseFloat(m.longitude)) : '',
    });
  };

  const handleEditSave = async e => {
    e.preventDefault(); setEditSaving(true);
    try {
      await api.patch(`/locker/admin/machines/${editMachine.id}`, {
        name: editForm.name, unit_id: editForm.unit_id, address: editForm.address,
        phone: editForm.phone, opening_hours: editForm.opening_hours, notes: editForm.notes,
        latitude:  editForm.latitude  ? parseFloat(editForm.latitude)  : '',
        longitude: editForm.longitude ? parseFloat(editForm.longitude) : '',
      });
      toast.success(t('admin.lockers.saved')); setEditMachine(null); loadMachines();
    } catch (err) { toast.error(err.error || t('admin.lockers.error')); }
    finally { setEditSaving(false); }
  };

  const handleMarkerDragEnd = async (machineId, e) => {
    const { lat, lng } = e.target.getLatLng();
    try {
      await api.patch(`/locker/admin/machines/${machineId}`, {
        latitude: parseFloat(lat.toFixed(6)), longitude: parseFloat(lng.toFixed(6)),
      });
      toast.success(t('admin.lockers.locationUpdated')); loadMachines();
    } catch { toast.error(t('admin.lockers.locationUpdateFailed')); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleteLoading(true);
    try {
      await api.delete(`/locker/admin/machines/${deleteTarget.id}`);
      toast.success(t('admin.lockers.machineDeleted', { name: deleteTarget.name }));
      setDeleteTarget(null); loadMachines();
    } catch (err) { toast.error(err.error || t('admin.lockers.error')); }
    finally { setDeleteLoading(false); }
  };

  const mappable = machines.filter(m => m.latitude && m.longitude);
  const noCoords = machines.filter(m => !m.latitude || !m.longitude);
  const mapCenter = mappable.length > 0
    ? [mappable.reduce((s,m) => s + parseFloat(m.latitude), 0) / mappable.length,
       mappable.reduce((s,m) => s + parseFloat(m.longitude), 0) / mappable.length]
    : DEFAULT_CENTER;

  const activeCount = machines.filter(m => m.is_active !== false).length;

  return (
    <div style={{ minHeight: '100vh', background: theme.creamDeep, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px 48px', flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 36 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ fontFamily: theme.font.display, fontSize: 44, color: C.dark, margin: '0 0 6px', fontWeight: 500, letterSpacing: '-0.01em' }}>
                {t('admin.lockers.title')}
              </h1>
              <p style={{ color: C.muted, margin: 0, fontSize: 13 }}>{t('admin.lockers.subtitle')}</p>
            </div>
            <button onClick={() => { setForm(EMPTY_FORM); setPendingPin(null); setShowAdd(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.dark, color: theme.white, border: 'none', padding: '13px 22px', borderRadius: 8, cursor: 'pointer', fontSize: 12, letterSpacing: '0.12em', fontFamily: theme.font.body, fontWeight: 500, boxShadow: `0 2px 8px ${theme.alpha.black(0.18)}` }}>
              <Plus size={15} /> {t('admin.lockers.newMachine')}
            </button>
          </div>

          {/* Stats row */}
          {!loading && machines.length > 0 && (
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              {[
                { id: 'machines', label: t('admin.lockers.statMachines'), value: machines.length, color: C.dark },
                { id: 'active', label: t('admin.lockers.statActive'), value: activeCount, color: C.green },
                { id: 'onmap', label: t('admin.lockers.statOnMap'), value: mappable.length, color: C.blue },
                ...(noCoords.length > 0 ? [{ id: 'nomap', label: t('admin.lockers.statNoMap'), value: noCoords.length, color: theme.amberDeep }] : []),
              ].map(s => (
                <div key={s.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 20px', minWidth: 100 }}>
                  <div style={{ fontFamily: theme.font.display, fontSize: 30, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4, letterSpacing: '0.04em' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Tabs ───────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: `1px solid ${C.border}` }}>
          {[
            { key:'map',       icon:<MapIcon size={14}/>,  label:t('admin.lockers.tabMap') },
            { key:'list',      icon:<List size={14}/>,     label:t('admin.lockers.tabList') },
            { key:'pickups',   icon:<KeyRound size={14}/>, label:t('admin.lockers.tabPickups') },
            { key:'callbacks', icon:<Activity size={14}/>, label:t('admin.lockers.tabCallbacks') },
          ].map(tabItem => (
            <button key={tabItem.key} onClick={() => setTab(tabItem.key)}
              style={{ display:'flex', alignItems:'center', gap:7, padding:'11px 26px', background:'none', border:'none', cursor:'pointer', fontSize:12, letterSpacing:'0.1em', fontFamily:theme.font.body, fontWeight: tab===tabItem.key ? 500 : 400, color: tab===tabItem.key ? C.dark : C.muted, borderBottom: tab===tabItem.key ? `2px solid ${C.gold}` : '2px solid transparent', marginBottom: -1 }}>
              {tabItem.icon}{tabItem.label}
            </button>
          ))}
        </div>

        {/* ── MAP TAB ────────────────────────────────────────────────────── */}
        {tab === 'map' && (
          <div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, boxShadow: `0 4px 20px ${theme.alpha.black(0.08)}`, height: 580 }}>
              <MapContainer center={mapCenter} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapClickHandler onMapClick={handleMapClick} />
                <FlyToHandler target={flyTarget} />

                {mappable.map(m => (
                  <Marker key={m.id} position={[parseFloat(m.latitude), parseFloat(m.longitude)]}
                    icon={goldIcon} draggable
                    eventHandlers={{ dragend: e => handleMarkerDragEnd(m.id, e) }}>
                    <Popup>
                      <div style={{ fontFamily: theme.font.body, minWidth: 210, padding: '2px 0' }}>
                        <div style={{ fontFamily: theme.font.display, fontSize: 18, color: C.dark, marginBottom: 2, fontWeight: 500 }}>{m.name}</div>
                        {m.address && <div style={{ fontSize: 11, color: theme.grey450, marginBottom: 8, lineHeight: 1.4 }}>{m.address}</div>}
                        {(m.phone || m.opening_hours) && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10, fontSize: 11, color: C.mid }}>
                            {m.phone && <span>📞 {m.phone}</span>}
                            {m.opening_hours && <span>🕐 {m.opening_hours}</span>}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                          <span style={{ background: C.faint, borderRadius: 6, padding: '4px 8px', fontSize: 11, fontFamily: theme.font.mono, color: C.mid }}>
                            {m.unit_id}
                          </span>
                          <span style={{ background: C.faint, borderRadius: 6, padding: '4px 8px', fontSize: 11, color: m.is_active !== false ? C.green : C.muted, fontWeight: 500 }}>
                            {m.is_active !== false ? t('admin.lockers.statusActive') : t('admin.lockers.statusInactive')}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => openEdit(m, { stopPropagation: () => {} })} style={mapPopupBtn(C.dark)}>
                            <Pencil size={11} /> {t('admin.lockers.edit')}
                          </button>
                          <button onClick={() => setDeleteTarget(m)} style={mapPopupBtn(C.red)}>
                            <Trash2 size={11} /> {t('admin.lockers.delete')}
                          </button>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 10, color: theme.taupeSoft, letterSpacing: '0.04em' }}>{t('admin.lockers.dragToMove')}</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {pendingPin && (
                  <Marker position={[pendingPin.lat, pendingPin.lng]} icon={blueIcon}>
                    <Popup>{t('admin.lockers.newMachineHere')}</Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
                {t('admin.lockers.mapHint')}
              </p>
              {noCoords.length > 0 && (
                <span style={{ fontSize: 12, color: theme.amberDeep, background: theme.amberWash, padding: '5px 12px', borderRadius: 20 }}>
                  {t('admin.lockers.noCoordsWarning', { count: noCoords.length })}
                  <button onClick={() => setTab('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.amberDeep, textDecoration: 'underline', padding: '0 0 0 6px', fontSize: 12 }}>{t('admin.lockers.goToList')}</button>
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── LIST TAB ───────────────────────────────────────────────────── */}
        {tab === 'list' && (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 100, color: C.gold, fontFamily: theme.font.display, fontSize: 26 }}>{t('admin.lockers.loading')}</div>
            ) : loadError ? (
              <ErrorPanel title={t('admin.lockers.loadFailedTitle')} detail={loadError}
                retryLabel={t('admin.lockers.retry')} onRetry={loadMachines} />
            ) : machines.length === 0 ? (
              <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 80, textAlign: 'center' }}>
                <div style={{ fontFamily: theme.font.display, fontSize: 28, color: C.muted, marginBottom: 8 }}>{t('admin.lockers.emptyTitle')}</div>
                <p style={{ color: C.muted, marginBottom: 24, fontSize: 14 }}>{t('admin.lockers.emptySubtitle')}</p>
                <button onClick={() => { setForm(EMPTY_FORM); setPendingPin(null); setShowAdd(true); }}
                  style={{ background: C.dark, color: theme.white, border: 'none', padding: '14px 32px', borderRadius: 8, cursor: 'pointer', fontSize: 13, letterSpacing: '0.1em' }}>
                  {t('admin.lockers.emptyCta')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {machines.map(m => (
                  <div key={m.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: `0 2px 8px ${theme.alpha.black(0.04)}` }}>

                    {/* Card header */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '18px 24px', gap: 20 }}>
                      {/* Gold accent bar */}
                      <div style={{ width: 4, alignSelf: 'stretch', background: m.is_active !== false ? C.gold : theme.grey240, borderRadius: 4, flexShrink: 0 }} />

                      {/* Info — clickable to expand details */}
                      <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => toggleExpand(m.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <div style={{ fontFamily: theme.font.display, fontSize: 22, color: C.dark, fontWeight: 500, lineHeight: 1.1 }}>{m.name}</div>
                          <HealthPill machine={m} t={t} />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 14px', marginTop: 5, fontSize: 12, color: C.muted }}>
                          {m.address && <span>{m.address}</span>}
                          <span style={{ fontFamily: theme.font.mono, background: C.faint, padding: '1px 7px', borderRadius: 4, fontSize: 11, color: C.mid }}>{m.unit_id}</span>
                          {m.latitude && m.longitude
                            ? <span style={{ color: C.gold }}>📍 {parseFloat(m.latitude).toFixed(4)}, {parseFloat(m.longitude).toFixed(4)}</span>
                            : <span style={{ color: theme.amberDeep }}>{t('admin.lockers.noCoords')}</span>}
                        </div>
                        {(m.phone || m.opening_hours) && (
                          <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: theme.grey550 }}>
                            {m.phone && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Phone size={11} style={{ color: C.gold }}/>{m.phone}</span>}
                            {m.opening_hours && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Clock size={11} style={{ color: C.gold }}/>{m.opening_hours}</span>}
                          </div>
                        )}
                      </div>

                      {/* Status */}
                      <div style={{ flexShrink: 0 }}>
                        <span style={{ fontSize: 11, padding: '5px 12px', borderRadius: 12, background: m.is_active !== false ? theme.greenWashSoft : C.faint, color: m.is_active !== false ? C.green : C.muted, fontWeight: 500, letterSpacing: '0.04em' }}>
                          {m.is_active !== false ? t('admin.lockers.statusActive') : t('admin.lockers.statusInactive')}
                        </span>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <IconBtn onClick={e => handleCheckNow(m, e)} title={t('admin.lockers.checkNow')} disabled={checkingId === m.id}>
                          <Wifi size={14} />
                        </IconBtn>
                        <IconBtn onClick={e => openEdit(m, e)} title={t('admin.lockers.edit')}><Pencil size={14} /></IconBtn>
                        <IconBtn onClick={() => setDeleteTarget(m)} title={t('admin.lockers.delete')} danger><Trash2 size={14} /></IconBtn>
                        <button onClick={() => toggleExpand(m.id)} title={t('admin.lockers.detailsToggle')} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', cursor: 'pointer', color: C.muted, fontSize: 12 }}>
                          {expanded === m.id ? '▲' : '▼'}
                        </button>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {expanded === m.id && (
                      <div style={{ borderTop: `1px solid ${C.faint}`, padding: '20px 24px' }}>
                        {m.notes && (
                          <div style={{ display: 'flex', gap: 10, background: C.faint, borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: C.mid, lineHeight: 1.5 }}>
                            <FileText size={15} style={{ flexShrink: 0, marginTop: 1, color: C.gold }} />
                            {m.notes}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: C.muted, letterSpacing: '0.12em', fontWeight: 500, marginBottom: 8 }}>
                          {t('admin.lockers.unlockHeading')}
                        </div>
                        <p style={{ margin: '0 0 12px', fontSize: 12, color: C.muted, lineHeight: 1.5, maxWidth: 620 }}>
                          {t('admin.lockers.unlockHint')}
                        </p>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            value={boxInput[m.id] || ''}
                            onChange={e => setBoxInput(p => ({ ...p, [m.id]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') handleUnlock(m); }}
                            placeholder={t('admin.lockers.boxNamePlaceholder')}
                            aria-label={t('admin.lockers.fieldBoxName')}
                            style={{ ...inputSt, width: 180 }}
                          />
                          <button onClick={() => handleUnlock(m)} disabled={unlocking === m.id}
                            style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.dark, color: theme.white, border: 'none', padding: '11px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: '0.08em', fontFamily: theme.font.body, fontWeight: 500, opacity: unlocking === m.id ? 0.6 : 1 }}>
                            <Unlock size={13} /> {unlocking === m.id ? t('admin.lockers.unlockOpening') : t('admin.lockers.unlockBtn')}
                          </button>
                        </div>

                        {/* ── The cabinet's own screen ────────────────────── */}
                        <ScreenPanel
                          machine={m}
                          state={screen[m.id]}
                          onRetry={() => loadScreen(m.id)}
                          onField={(f, v) => setScreenField(m.id, f, v)}
                          onPush={() => pushScreen(m)}
                          t={t}
                          locale={getIntlLocale()}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── PICKUPS TAB ────────────────────────────────────────────────── */}
        {/* The recovery endpoint existed but nothing in the app called it, so the
            ops email saying "re-run the pickup registration from the admin panel"
            pointed at a panel that did not exist. This is it. */}
        {tab === 'pickups' && (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: `0 2px 8px ${theme.alpha.black(0.04)}` }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '18px 24px', borderBottom: `1px solid ${C.faint}` }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontFamily: theme.font.display, fontSize: 22, color: C.dark, fontWeight: 500, lineHeight: 1.1 }}>
                  {t('admin.lockers.pickupsTitle')}
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted, maxWidth: 640, lineHeight: 1.5 }}>{t('admin.lockers.pickupsSubtitle')}</p>
              </div>
              <button onClick={loadPickups} disabled={pkLoading}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontFamily: theme.font.body }}>
                <RefreshCw size={12} /> {t('admin.lockers.refresh')}
              </button>
            </div>

            {pkLoading ? (
              <div style={{ textAlign: 'center', padding: 60, color: C.gold, fontFamily: theme.font.display, fontSize: 26 }}>{t('admin.lockers.loading')}</div>
            ) : pkError ? (
              <ErrorPanel title={t('admin.lockers.loadFailedTitle')} detail={pkError}
                retryLabel={t('admin.lockers.retry')} onRetry={loadPickups} />
            ) : pickups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <div style={{ fontFamily: theme.font.display, fontSize: 24, color: C.green, marginBottom: 6 }}>{t('admin.lockers.pickupsEmptyTitle')}</div>
                <p style={{ color: C.muted, margin: 0, fontSize: 13 }}>{t('admin.lockers.pickupsEmptyHint')}</p>
              </div>
            ) : (
              <div>
                {pickups.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '14px 24px', borderBottom: `1px solid ${C.faint}`, flexWrap: 'wrap' }}>
                    <AlertTriangle size={15} style={{ color: C.amber, flexShrink: 0, marginTop: 3 }} />
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ fontSize: 13, color: C.dark }}>
                        {r.product_name || '—'}
                        <span style={{ color: C.muted }}> · {r.full_name || '—'} {r.email ? `<${r.email}>` : ''}</span>
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 3, fontFamily: theme.font.mono }}>{r.id}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                        {t('admin.lockers.pickupPaidAt')}: {r.rental_paid_at ? new Date(r.rental_paid_at).toLocaleString(getIntlLocale()) : '—'}
                        {' · '}{t('admin.lockers.pickupAttempts')}: {r.pickup_register_attempts ?? 0}
                      </div>
                      {r.pickup_register_last_error && (
                        <div style={{ fontSize: 11, color: C.red, marginTop: 4, lineHeight: 1.4 }}>{r.pickup_register_last_error}</div>
                      )}
                    </div>
                    <button onClick={() => registerPickup(r.id)} disabled={registering === r.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.dark, color: theme.white, border: 'none', padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body, fontWeight: 500, opacity: registering === r.id ? 0.6 : 1 }}>
                      <KeyRound size={13} /> {registering === r.id ? t('admin.lockers.pickupRegistering') : t('admin.lockers.pickupRegisterBtn')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Manual escape hatch: a rental that already HAS a code but whose box
                was never actually reserved (or was reserved on the wrong cabinet)
                will never appear in the list above. */}
            <div style={{ padding: '18px 24px', borderTop: `1px solid ${C.faint}`, background: C.faint }}>
              <div style={{ fontSize: 11, color: C.muted, letterSpacing: '0.12em', fontWeight: 500, marginBottom: 8 }}>
                {t('admin.lockers.pickupManualHeading')}
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: C.muted, lineHeight: 1.5, maxWidth: 640 }}>
                {t('admin.lockers.pickupManualHint')}
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={manualId} onChange={e => setManualId(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') registerPickup(manualId, manualForce); }}
                  placeholder={t('admin.lockers.pickupIdPlaceholder')}
                  aria-label={t('admin.lockers.pickupIdLabel')}
                  style={{ ...inputSt, width: 340, background: C.white }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.mid, cursor: 'pointer' }}>
                  <input type="checkbox" checked={manualForce} onChange={e => setManualForce(e.target.checked)} />
                  {t('admin.lockers.pickupForce')}
                </label>
                <button onClick={() => registerPickup(manualId, manualForce)} disabled={registering === manualId.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.dark, color: theme.white, border: 'none', padding: '11px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: '0.08em', fontFamily: theme.font.body, fontWeight: 500 }}>
                  <KeyRound size={13} /> {t('admin.lockers.pickupRegisterBtn')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── CALLBACKS TAB ──────────────────────────────────────────────── */}
        {tab === 'callbacks' && (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: `0 2px 8px ${theme.alpha.black(0.04)}` }}>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '18px 24px', borderBottom: `1px solid ${C.faint}` }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontFamily: theme.font.display, fontSize: 22, color: C.dark, fontWeight: 500, lineHeight: 1.1 }}>
                  {t('admin.lockers.callbacksTitle')}
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted }}>{t('admin.lockers.callbacksSubtitle')}</p>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                {['all', 'save', 'take', 'invalid'].map(f => (
                  <button key={f} onClick={() => setCbFilter(f)}
                    style={{ padding: '7px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 11, letterSpacing: '0.06em', fontFamily: theme.font.body,
                      border: `1px solid ${cbFilter === f ? C.gold : C.border}`,
                      background: cbFilter === f ? C.goldLight : C.white,
                      color: cbFilter === f ? C.dark : C.muted }}>
                    {t(`admin.lockers.cbFilter_${f}`)}
                  </button>
                ))}
              </div>

              <button onClick={() => loadCallbacks(cbFilter)} disabled={cbLoading}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontFamily: theme.font.body }}>
                <RefreshCw size={12} /> {t('admin.lockers.refresh')}
              </button>
            </div>

            {/* Signature config strip */}
            {cbMeta && (
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: '10px 24px', background: C.faint, fontSize: 11, color: C.mid }}>
                <span>{t('admin.lockers.cbSignatureMode')}: <strong style={{ fontFamily: theme.font.mono, color: C.dark }}>{cbMeta.signature_mode}</strong></span>
                <span>{t('admin.lockers.cbMaxAge')}: <strong style={{ fontFamily: theme.font.mono, color: C.dark }}>{cbMeta.max_age_seconds}s</strong></span>
              </div>
            )}

            {cbLoading ? (
              <div style={{ textAlign: 'center', padding: 80, color: C.gold, fontFamily: theme.font.display, fontSize: 26 }}>{t('admin.lockers.loading')}</div>
            ) : callbacks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 80 }}>
                <div style={{ fontFamily: theme.font.display, fontSize: 26, color: C.muted, marginBottom: 8 }}>{t('admin.lockers.callbacksEmptyTitle')}</div>
                <p style={{ color: C.muted, margin: 0, fontSize: 13 }}>{t('admin.lockers.callbacksEmptyHint')}</p>
              </div>
            ) : (
              <div>
                {callbacks.map(cb => (
                  <div key={cb.id} style={{ borderBottom: `1px solid ${C.faint}` }}>
                  {/* The whole point of observe mode is comparing the signature the
                      machine sent against our candidate digests. The API has always
                      returned body/raw_body/query/headers/signature_candidates; the
                      panel showed none of it, so the one screen built for install day
                      could not answer the one question install day asks. */}
                  <div onClick={() => setCbExpanded(p => (p === cb.id ? null : cb.id))}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '14px 24px', flexWrap: 'wrap', cursor: 'pointer' }}>

                    <span style={{ color: C.muted, paddingTop: 2 }}>
                      {cbExpanded === cb.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>

                    <span style={{ fontSize: 11, color: C.muted, minWidth: 132, fontFamily: theme.font.mono, paddingTop: 3 }}>
                      {cb.received_at ? new Date(cb.received_at).toLocaleString(getIntlLocale()) : '—'}
                    </span>

                    <span style={{ fontSize: 10, padding: '4px 10px', borderRadius: 12, fontWeight: 600, letterSpacing: '0.06em', minWidth: 52, textAlign: 'center',
                      background: (ENDPOINT_CFG[cb.endpoint] || {}).bg || C.faint,
                      color: (ENDPOINT_CFG[cb.endpoint] || {}).color || C.mid }}>
                      {(cb.endpoint || '?').toUpperCase()}
                    </span>

                    <span style={{ flex: 1, minWidth: 180, fontSize: 12, color: C.dark, paddingTop: 2 }}>
                      <span style={{ fontFamily: theme.font.mono }}>{cb.order_no || t('admin.lockers.cbNoOrder')}</span>
                      {cb.event && <span style={{ color: C.muted }}> · {cb.event}</span>}
                      {cb.error && <div style={{ fontSize: 11, color: C.red, marginTop: 3, lineHeight: 1.4 }}>{cb.error}</div>}
                    </span>

                    <span title={cb.signature_scheme || ''} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, minWidth: 120, paddingTop: 3, color: cb.signature_valid ? C.green : C.red }}>
                      {cb.signature_valid ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />}
                      {cb.signature_valid
                        ? (cb.signature_scheme || t('admin.lockers.cbSigValid'))
                        : t('admin.lockers.cbSigInvalid')}
                    </span>

                    <span style={{ fontSize: 11, minWidth: 150, paddingTop: 3, color: OUTCOME_COLOR(cb.outcome), fontFamily: theme.font.mono }}>
                      {cb.outcome || '—'}
                    </span>
                  </div>

                  {cbExpanded === cb.id && (
                    <div style={{ padding: '4px 24px 20px 54px', background: C.faint }}>
                      {cb.signature_candidates && (
                        <SignatureCompare candidates={cb.signature_candidates} t={t} />
                      )}
                      <JsonBlock label={t('admin.lockers.cbBody')} value={cb.body} />
                      <JsonBlock label={t('admin.lockers.cbRawBody')} value={cb.raw_body} raw />
                      <JsonBlock label={t('admin.lockers.cbQuery')} value={cb.query} />
                      <JsonBlock label={t('admin.lockers.cbHeaders')} value={cb.headers} />
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 10, fontFamily: theme.font.mono }}>
                        id {cb.id}{cb.remote_ip ? ` · ip ${cb.remote_ip}` : ''}
                      </div>
                    </div>
                  )}
                  </div>
                ))}
                <div style={{ padding: '12px 24px', fontSize: 11, color: C.muted }}>
                  {t('admin.lockers.cbShowing', { count: callbacks.length })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── ADD MODAL ───────────────────────────────────────────────────────── */}
      {showAdd && (
        <Modal title={t('admin.lockers.addTitle')}
          subtitle={pendingPin ? `📍 ${pendingPin.lat.toFixed(5)}, ${pendingPin.lng.toFixed(5)}` : t('admin.lockers.addSubtitle')}
          onClose={closeAdd}>
          <form onSubmit={handleAddMachine}>
            <Section label={t('admin.lockers.sectionBasics')}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <FField label={t('admin.lockers.fieldName')} icon={<Hash size={13}/>} placeholder={t('admin.lockers.namePlaceholder')} value={form.name} onChange={v=>setForm({...form,name:v})} required />
                <FField label={t('admin.lockers.fieldUnitId')} icon={<Hash size={13}/>} placeholder={t('admin.lockers.unitIdPlaceholder')} value={form.unit_id} onChange={v=>setForm({...form,unit_id:v})} required hint={t('admin.lockers.unitIdHint')} />
              </div>
            </Section>

            <Section label={t('admin.lockers.sectionLocation')}>
              <AddressSearch value={form.address} onChange={v=>setForm({...form,address:v})}
                onPick={r => geoApply(r, p=>setForm(f=>({...f,...p})), setPendingPin)} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:12 }}>
                <FField label={t('admin.lockers.fieldLatitude')}  placeholder="35.1629" value={form.latitude}  onChange={v=>setForm({...form,latitude:v})}  hint={pendingPin?t('admin.lockers.fromMap'):t('admin.lockers.optional')} />
                <FField label={t('admin.lockers.fieldLongitude')} placeholder="33.3622" value={form.longitude} onChange={v=>setForm({...form,longitude:v})} hint={pendingPin?t('admin.lockers.fromMap'):t('admin.lockers.orClickMap')} />
              </div>
            </Section>

            <Section label={t('admin.lockers.sectionContact')}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <FField label={t('admin.lockers.fieldPhone')} icon={<Phone size={13}/>} placeholder="+357 22 123456" value={form.phone} onChange={v=>setForm({...form,phone:v})} />
                <FField label={t('admin.lockers.fieldHours')} icon={<Clock size={13}/>} placeholder="08:00-22:00" value={form.opening_hours} onChange={v=>setForm({...form,opening_hours:v})} />
              </div>
              <div style={{marginTop:12}}>
                <FField label={t('admin.lockers.fieldNotes')} icon={<FileText size={13}/>} placeholder={t('admin.lockers.notesPlaceholder')} value={form.notes} onChange={v=>setForm({...form,notes:v})} multiline />
              </div>
            </Section>

            <button type="submit" disabled={saving}
              style={{ width:'100%', marginTop:28, padding:'15px', background:C.dark, color:theme.white, border:'none', borderRadius:8, fontSize:13, letterSpacing:'0.12em', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontFamily:theme.font.body, fontWeight:500 }}>
              <Save size={15}/> {saving?t('admin.lockers.saving'):t('admin.lockers.save')}
            </button>
          </form>
        </Modal>
      )}

      {/* ── EDIT MODAL ──────────────────────────────────────────────────────── */}
      {editMachine && (
        <Modal title={t('admin.lockers.editTitle')} subtitle={editMachine.name} onClose={()=>setEditMachine(null)}>
          <form onSubmit={handleEditSave}>
            <Section label={t('admin.lockers.sectionBasics')}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <FField label={t('admin.lockers.fieldName')} placeholder={t('admin.lockers.namePlaceholder')} value={editForm.name} onChange={v=>setEditForm({...editForm,name:v})} required />
                <FField label={t('admin.lockers.fieldUnitId')} placeholder={t('admin.lockers.unitIdPlaceholder')} value={editForm.unit_id} onChange={v=>setEditForm({...editForm,unit_id:v})} required hint={t('admin.lockers.unitIdHint')} />
              </div>
            </Section>

            <Section label={t('admin.lockers.sectionLocation')}>
              <AddressSearch value={editForm.address} onChange={v=>setEditForm({...editForm,address:v})}
                onPick={r => geoApply(r, p=>setEditForm(f=>({...f,...p})), null)} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:12 }}>
                <FField label={t('admin.lockers.fieldLatitude')}  placeholder="35.1629" value={editForm.latitude}  onChange={v=>setEditForm({...editForm,latitude:v})}  hint={t('admin.lockers.latHintEdit')} />
                <FField label={t('admin.lockers.fieldLongitude')} placeholder="33.3622" value={editForm.longitude} onChange={v=>setEditForm({...editForm,longitude:v})} hint={t('admin.lockers.lngHintEdit')} />
              </div>
            </Section>

            <Section label={t('admin.lockers.sectionContact')}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <FField label={t('admin.lockers.fieldPhone')} icon={<Phone size={13}/>} placeholder="+357 22 123456" value={editForm.phone} onChange={v=>setEditForm({...editForm,phone:v})} />
                <FField label={t('admin.lockers.fieldHours')} icon={<Clock size={13}/>} placeholder="08:00-22:00" value={editForm.opening_hours} onChange={v=>setEditForm({...editForm,opening_hours:v})} />
              </div>
              <div style={{marginTop:12}}>
                <FField label={t('admin.lockers.fieldNotes')} icon={<FileText size={13}/>} placeholder={t('admin.lockers.notesPlaceholder')} value={editForm.notes} onChange={v=>setEditForm({...editForm,notes:v})} multiline />
              </div>
            </Section>

            <button type="submit" disabled={editSaving}
              style={{ width:'100%', marginTop:28, padding:'15px', background:C.dark, color:theme.white, border:'none', borderRadius:8, fontSize:13, letterSpacing:'0.12em', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontFamily:theme.font.body, fontWeight:500 }}>
              <Save size={15}/> {editSaving?t('admin.lockers.saving'):t('admin.lockers.save')}
            </button>
          </form>
        </Modal>
      )}

      {/* ── DELETE CONFIRM ──────────────────────────────────────────────────── */}
      {deleteTarget && (
        <div style={{ position:'fixed', inset:0, background:theme.alpha.black(0.55), zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div style={{ background:C.white, borderRadius:16, width:'100%', maxWidth:420, padding:'40px 40px 36px', boxShadow:`0 20px 60px ${theme.alpha.black(0.2)}` }}>
            <div style={{ width:48, height:48, borderRadius:12, background:theme.redWashWarm, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
              <Trash2 size={22} color={C.red} />
            </div>
            <h2 style={{ fontFamily:theme.font.display, fontSize:26, margin:'0 0 10px', color:C.dark }}>{t('admin.lockers.deleteTitle')}</h2>
            <p style={{ color:C.mid, fontSize:14, lineHeight:1.6, margin:0 }}>
              <Trans
                i18nKey="admin.lockers.deleteConfirm"
                values={{ name: deleteTarget.name }}
                components={{ strong: <strong /> }}
              /><br/>
              <span style={{ fontSize:13, color:C.muted }}>{t('admin.lockers.deleteWarning')}</span>
            </p>
            <div style={{ display:'flex', gap:10, marginTop:28 }}>
              <button onClick={() => setDeleteTarget(null)}
                style={{ flex:1, padding:'13px', background:theme.white, border:`1px solid ${C.border}`, borderRadius:8, cursor:'pointer', fontSize:13, color:C.mid, fontFamily:theme.font.body }}>
                {t('admin.lockers.cancel')}
              </button>
              <button onClick={handleDelete} disabled={deleteLoading}
                style={{ flex:1, padding:'13px', background:C.red, color:theme.white, border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontFamily:theme.font.body, fontWeight:500 }}>
                {deleteLoading ? t('admin.lockers.deleting') : t('admin.lockers.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

// Shown instead of an empty state when a fetch fails, so "we could not load
// this" never renders as "there is nothing here".
function ErrorPanel({ title, detail, retryLabel, onRetry }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${theme.redWash}`, borderRadius: 12, padding: 48, textAlign: 'center' }}>
      <AlertTriangle size={26} style={{ color: C.red }} />
      <div style={{ fontFamily: theme.font.display, fontSize: 26, color: C.dark, margin: '10px 0 6px' }}>{title}</div>
      {detail && <p style={{ color: C.muted, margin: '0 0 20px', fontSize: 13 }}>{detail}</p>}
      <button onClick={onRetry}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: C.dark, color: theme.white, border: 'none', padding: '12px 26px', borderRadius: 8, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
        <RefreshCw size={13} /> {retryLabel}
      </button>
    </div>
  );
}

// jsonb columns arrive as objects; TEXT columns as strings. Both are shown
// verbatim — this is forensic data, so nothing here may be "helpfully" reshaped.
function JsonBlock({ label, value, raw }) {
  if (value === null || value === undefined || value === '') return null;
  const text = (raw || typeof value === 'string') ? String(value) : JSON.stringify(value, null, 2);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, color: C.muted, letterSpacing: '0.12em', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <pre style={{ margin: 0, padding: '10px 12px', background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, lineHeight: 1.5, color: C.dark, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 320, overflowY: 'auto' }}>
        {text}
      </pre>
    </div>
  );
}

// Observe mode records every digest we computed for a callback we rejected.
// Whichever one equals the `provided` value names the scheme the real cabinet
// uses — that comparison is the entire deliverable of install day, so it gets
// its own presentation rather than being buried in a JSON blob.
function SignatureCompare({ candidates, t }) {
  const data = typeof candidates === 'string' ? safeParse(candidates) : candidates;
  if (!data || !data.computed) return null;
  const provided = String(data.provided || '').trim().toLowerCase();
  const entries = Object.entries(data.computed);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, color: C.muted, letterSpacing: '0.12em', fontWeight: 600, marginBottom: 4 }}>
        {t('admin.lockers.cbSignatureCompare')}
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px' }}>
        <div style={{ fontSize: 11, color: C.dark, wordBreak: 'break-all' }}>
          <strong>{t('admin.lockers.cbSigProvided')}</strong>
          {data.provided_location ? ` (${data.provided_location})` : ''}:{' '}
          <span style={{ fontFamily: theme.font.mono }}>{data.provided}</span>
        </div>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entries.map(([scheme, digest]) => {
            const match = String(digest).toLowerCase() === provided;
            return (
              <div key={scheme} style={{ fontSize: 10, fontFamily: theme.font.mono, color: match ? C.green : C.mid, wordBreak: 'break-all' }}>
                {match ? '✔ ' : '  '}{scheme}: {digest}
              </div>
            );
          })}
        </div>
        {!entries.some(([, d]) => String(d).toLowerCase() === provided) && (
          <div style={{ marginTop: 8, fontSize: 11, color: C.amber }}>{t('admin.lockers.cbNoSchemeMatched')}</div>
        )}
      </div>
    </div>
  );
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}

function Modal({ title, subtitle, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:theme.alpha.black(0.5), zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:C.white, borderRadius:16, width:'100%', maxWidth:580, padding:'36px 40px', maxHeight:'92vh', overflowY:'auto', boxShadow:`0 24px 64px ${theme.alpha.black(0.18)}` }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
          <div>
            <h2 style={{ fontFamily:theme.font.display, fontSize:30, margin:0, color:C.dark, fontWeight:500 }}>{title}</h2>
            {subtitle && <p style={{ margin:'5px 0 0', fontSize:12, color:C.gold }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:4 }}><X size={20}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        <span style={{ fontSize:10, letterSpacing:'0.16em', color:C.muted, whiteSpace:'nowrap', fontWeight:600 }}>{label}</span>
        <div style={{ flex:1, height:1, background:C.border }}/>
      </div>
      {children}
    </div>
  );
}

const inputSt = { width:'100%', padding:'11px 14px', border:`1px solid ${C.border}`, borderRadius:6, fontSize:13, fontFamily:theme.font.body, boxSizing:'border-box', outline:'none', color:C.dark, background:C.white };

function FieldLabel({ children, icon }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.muted, letterSpacing:'0.1em', marginBottom:6, fontWeight:500 }}>
      {icon && <span style={{ color:C.gold }}>{icon}</span>}
      {children}
    </label>
  );
}

function FField({ label, icon, value, onChange, placeholder, required, hint, type='text', min, max, multiline }) {
  return (
    <div>
      <FieldLabel icon={icon}>{label}</FieldLabel>
      {multiline
        ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={3}
            style={{ ...inputSt, resize:'vertical', lineHeight:1.5 }} />
        : <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
            required={required} type={type} min={min} max={max} style={inputSt} />
      }
      {hint && <p style={{ margin:'5px 0 0', fontSize:11, color:C.muted }}>{hint}</p>}
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, disabled }) {
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      style={{ background:'none', border:`1px solid ${danger ? theme.redBorderBlush : C.border}`, borderRadius:6, padding:'8px 10px', cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.5 : 1, color: danger ? C.red : C.mid, display:'flex', alignItems:'center' }}>
      {children}
    </button>
  );
}

// What the cabinet shows on its own screen.
//
// Bestwond's screen API takes exactly three strings — a QR URL, an address line
// and a help phone — and there is no call that reads them back. Everything shown
// here is therefore "what we last sent", never "what the machine is displaying",
// and it says so: a panel that implied otherwise would let a cabinet advertise a
// disconnected phone number indefinitely with a green tick next to it.
function ScreenPanel({ machine, state, onRetry, onField, onPush, t, locale }) {
  const s = state || {};

  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.faint}` }}>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: '0.12em', fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Monitor size={12} style={{ color: C.gold }} /> {t('admin.lockers.screenHeading')}
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: C.muted, lineHeight: 1.5, maxWidth: 620 }}>
        {t('admin.lockers.screenHint')}
      </p>

      {s.loading && (
        <div style={{ fontSize: 12, color: C.muted }}>{t('admin.lockers.screenLoading')}</div>
      )}

      {!s.loading && s.error && (
        <ErrorPanel title={t('admin.lockers.screenLoadFailed')} detail={s.error}
          retryLabel={t('admin.lockers.retry')} onRetry={onRetry} />
      )}

      {!s.loading && !s.error && s.form && (
        <>
          {/* Simulation is the state the system is in until the credentials
              exist, and a "pushed" toast that reached no hardware is exactly the
              kind of false comfort that gets discovered at the machine. */}
          {s.simulate && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: theme.amberWash, border: `1px solid ${theme.amber}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: theme.amberDeep, lineHeight: 1.5, maxWidth: 620 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              {t('admin.lockers.screenSimulated')}
            </div>
          )}

          <div style={{ display: 'grid', gap: 14, maxWidth: 520 }}>
            <FField
              label={t('admin.lockers.screenQr')} icon={<Hash size={11} />}
              value={s.form.qr} onChange={v => onField('qr', v)}
              hint={t('admin.lockers.screenQrHint')} placeholder="https://glamgo.hair"
            />
            <FField
              label={t('admin.lockers.screenAddress')} icon={<MapPin size={11} />}
              value={s.form.device_address} onChange={v => onField('device_address', v)}
              hint={t('admin.lockers.screenAddressHint')}
            />
            <FField
              label={t('admin.lockers.screenPhone')} icon={<Phone size={11} />}
              value={s.form.app_kf_mobile} onChange={v => onField('app_kf_mobile', v)}
              hint={t('admin.lockers.screenPhoneHint')}
            />
          </div>

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
            <button onClick={onPush} disabled={s.saving}
              style={{ display: 'flex', alignItems: 'center', gap: 7, background: C.dark, color: theme.white, border: 'none', padding: '11px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: '0.08em', fontFamily: theme.font.body, fontWeight: 500, opacity: s.saving ? 0.6 : 1 }}>
              <Monitor size={13} /> {s.saving ? t('admin.lockers.screenPushing') : t('admin.lockers.screenPushBtn')}
            </button>

            <span style={{ fontSize: 11, color: C.muted }}>
              {s.pushed
                ? t('admin.lockers.screenLastPushed', {
                    when: new Date(s.pushed.at).toLocaleString(locale),
                    who: s.pushed.by || '—',
                  }) + (s.pushed.simulated ? ` · ${t('admin.lockers.screenLastPushedSim')}` : '')
                : t('admin.lockers.screenNeverPushed')}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function mapPopupBtn(color) {
  return { padding:'6px 11px', fontSize:11, border:`1px solid ${color}`, borderRadius:6, color, background:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontFamily:theme.font.body };
}

// Reachability of a cabinet, as last observed by the health cron.
//
// 'unknown' is shown as its own state rather than folded into "offline", and
// that distinction is the point. The device-status endpoint path was never
// verified against the real API (docs/LOCKER_INSTALL.md), so until it is
// corrected 'unknown' is the expected answer — and telling an operator a working
// cabinet is down would send them on a wasted drive and teach them to ignore the
// indicator, which is worse than showing nothing at all.
function HealthPill({ machine, t }) {
  const status = machine.health_status || 'unknown';
  if (machine.is_active === false) return null;

  const style = {
    online:  { bg: theme.greenWashSoft, fg: theme.green, dot: theme.greenLight },
    offline: { bg: theme.redWashWarm, fg: theme.redSoft, dot: theme.redSoft },
    unknown: { bg: theme.cream, fg: theme.grey450, dot: theme.grey300 },
  }[status] || { bg: theme.cream, fg: theme.grey450, dot: theme.grey300 };

  const since = machine.last_seen_at
    ? new Date(machine.last_seen_at).toLocaleString(getIntlLocale())
    : null;

  const title = status === 'unknown'
    ? t('admin.lockers.healthUnknownHint')
    : since
      ? t('admin.lockers.healthLastSeen', { when: since })
      : t('admin.lockers.healthNeverSeen');

  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: style.bg, color: style.fg,
        padding: '3px 10px', borderRadius: 999,
        fontSize: 11, letterSpacing: '0.04em', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: style.dot }} />
      {t(`admin.lockers.health.${status}`)}
    </span>
  );
}
